/**
 * StreakDisplay component
 * Displays the first-try streak count with a lightning icon
 * Used in the status strip on the My Sessions page
 */

import { cn } from '@/lib/utils';
import { Zap } from 'lucide-react';

export interface StreakDisplayProps {
  /** First-try streak count */
  streak: number;
  /** Size variant: 'sm' | 'md' | 'lg' (default: 'md') */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * StreakDisplay - Displays lightning icon with streak count
 * Follows same pattern as PebblesDisplay for consistency
 */
export function StreakDisplay({ streak, size = 'md' }: StreakDisplayProps) {
  // Ensure streak is never negative
  const count = Math.max(0, streak ?? 0);

  const sizeClasses = {
    sm: { icon: 'w-4 h-4', text: 'text-sm', gap: 'gap-1' },
    md: { icon: 'w-5 h-5', text: 'text-base font-semibold', gap: 'gap-1.5' },
    lg: { icon: 'w-6 h-6', text: 'text-lg font-bold', gap: 'gap-2' },
  };

  const classes = sizeClasses[size];

  return (
    <div
      className={cn('inline-flex items-center', classes.gap)}
      role="status"
      aria-label={`${count} first-try streak`}
    >
      <Zap className={cn(classes.icon, 'text-otter-orange-500')} aria-hidden="true" />
      <span className={cn('text-gray-600', classes.text)}>{count}</span>
    </div>
  );
}
